import { Ref, ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  getWatchlists,
  createWatchlist,
  renameWatchlist as renameWatchlistReq,
  addCompaniesToWatchlist as addCompaniesToWatchlistReq,
  deleteWatchlist as deleteWatchlistReq,
  removeCompanyFromWatchlist as removeCompanyFromWatchlistReq,
  Watchlist,
  Message,
} from '@cognitivecredit/ui-utils'
import { License } from '@/stores/User/state'
import * as gqlQuery from '@/stores/Company/queries.graphql'
import { useGraphQL } from '@/composables/graphQL'

import store from '@/stores/index'
import { SET_SELECTED_UNIVERSES } from '@/stores/User/mutations'

// Step 1 - Enter Name,
// Step 2, pick whether you filter or bulk add or skip
// Step 3 is bulk add
// Step 4 is filter
// step 5 is review bulk add
// step 6 is review filter
export enum AddWatchlistStep {
  ENTER_NAME = 1,
  BULK_OR_FILTER = 2,
  BULK_ADD = 3,
  FILTER = 4,
  BULK_ADD_REVIEW = 5,
  FILTER_REVIEW = 6,
}
export const useWatchlistsStore = defineStore('watchlists', () => {
  const watchlists: Ref<Watchlist[]> = ref([])
  const activeWatchlists: Ref<string[]> = ref([])
  const watchlistMessage: Ref<Message | undefined> = ref(undefined)
  const addWatchlistModalStep: Ref<AddWatchlistStep> = ref(AddWatchlistStep.ENTER_NAME)
  const newWatchlistName: Ref<string> = ref('')
  const watchlistToDelete: Ref<undefined | string> = ref(undefined)
  const dismissWatchlistModalGuard: Ref<boolean> = ref(false)

  const setPreviousUniverses = () => {
    const { selectedUniverses } = store.getters['User/preferences']
    const universes = store.state.User.licenseList?.filter(
      (universe: License) => selectedUniverses.indexOf(universe.id) > -1 && universe,
    )
    store.commit(`User/${SET_SELECTED_UNIVERSES}`, universes, { root: true })
  }

  const loadWatchlists = async () => {
    const res = await getWatchlists()
    watchlists.value = res.data.watchlists
  }
  const createNewWatchlist = async (name: string) => {
    return createWatchlist(name)
  }
  const setAddWatchlistModalStep = (step: number) => {
    addWatchlistModalStep.value = step
  }
  const renameWatchlist = async (id: string, name: string) => {
    const watchlist = watchlists.value.find((w) => w.id === id)
    let oldName: string = ''
    if (watchlist) {
      oldName = watchlist.name
      watchlist.name = name
    }
    try {
      return renameWatchlistReq(id, name)
    } catch (e) {
      if (watchlist && oldName) {
        watchlist.name = oldName
      }
      console.error('`[ERROR] Renaming watchlist failed:', e)
    }
  }
  const addCompaniesToWatchlist = async (watchlistId: string, companyIds: string[]) => {
    const watchlist = watchlists.value.find((w) => w.id === watchlistId)
    const oldCompanies: string[] = watchlist ? [...watchlist.companies] : []
    try {
      if (watchlist) {
        watchlist.companies = Array.from(new Set([...watchlist.companies, ...companyIds]))
      }
      // not great but need to add this to the companies store too
      // to make the star in the company list update
      store.dispatch('Company/addToWatchlist', { companyId: companyIds[0], watchlistId: watchlistId })
      return addCompaniesToWatchlistReq(watchlistId, companyIds)
    } catch (e) {
      if (watchlist) {
        watchlist.companies = oldCompanies
      }
      console.error('`[ERROR] Adding companies to watchlist failed:', e)
    }
  }
  const deleteWatchlist = async (id: string) => {
    try {
      watchlistToDelete.value = undefined
      return deleteWatchlistReq(id)
    } catch (e) {
      console.error('`[ERROR] Deleting watchlist failed:', e)
    }
  }
  const resetMessage = () => {
    watchlistMessage.value = undefined
  }
  const removeCompanyFromWatchlist = async (watchlistId: string, companyId: string) => {
    const watchlist = watchlists.value.find((w) => w.id === watchlistId)
    try {
      if (watchlist) {
        watchlist.companies = watchlist.companies.filter((c) => c !== companyId)
      }
      // not great but need to add this to the companies store too
      // to make the star in the company list update
      store.dispatch('Company/removeFromWatchlist', { companyId, watchlistId })
      return removeCompanyFromWatchlistReq(watchlistId, companyId)
    } catch (e) {
      console.error('`[ERROR] Removing company from watchlist failed:', e)
      watchlist?.companies.push(companyId)
    }
  }
  const resetWatchlistModalState = () => {
    watchlistMessage.value = undefined
    setAddWatchlistModalStep(AddWatchlistStep.ENTER_NAME)
    newWatchlistName.value = ''
    dismissWatchlistModalGuard.value = false
  }
  const getFilteredCompanies = async (gicsCodes: string[], licenseIds: string[]) => {
    const companies: string[][] = []
    if (gicsCodes.length === 0) {
      const payload = {
        filter: {
          licenseId: licenseIds,
        },
        sort: { name: 'ASC' },
      }

      const { response } = await useGraphQL(gqlQuery.GetWatchlistFilteredCompanies, payload)
      companies.push(response.data?.filteredCompanies?.data || [])
    } else {
      for (let i = 0; i < gicsCodes.length; i++) {
        const payload = {
          filter: {
            licenseId: licenseIds,
          },
          search: {
            id: gicsCodes[i],
          },
          sort: { name: 'ASC' },
        }

        const { response } = await useGraphQL(gqlQuery.GetWatchlistFilteredCompanies, payload)
        companies.push(response.data?.filteredCompanies?.data || [])
      }
    }
    return [...new Set(companies.flat())]
  }
  const activateWatchlist = (id: string, exclusive = false) => {
    if (!activeWatchlists.value.includes(id)) {
      if (exclusive) {
        activeWatchlists.value = [id]
        return
      }
      activeWatchlists.value.push(id)
      if (store.getters['User/selectedUniverseIds'].length > 0) {
        store.commit(`User/${SET_SELECTED_UNIVERSES}`, [], { root: true })
      }
    }
  }
  const deactivateWatchlist = (id: string) => {
    activeWatchlists.value = activeWatchlists.value.filter((wid) => wid !== id)
    if (activeWatchlists.value.length === 0 && store.state.User.licenseList) {
      setPreviousUniverses()
    }
  }
  const disableWatchlists = (license?: License) => {
    activeWatchlists.value = []
    if (license?.id) {
      store.dispatch('User/saveNewPreference', {
        universes: [license.id],
      })
      store.commit(`User/${SET_SELECTED_UNIVERSES}`, [license], { root: true })
    } else {
      setPreviousUniverses()
    }
  }
  const anyWatchlistActive = computed(() => activeWatchlists.value.length > 0)
  const companiesInActiveWatchlists = computed(() => {
    const companies: Set<string> = new Set()
    watchlists.value.forEach((watchlist) => {
      if (activeWatchlists.value.includes(watchlist.id)) {
        watchlist.companies.forEach((company) => companies.add(company))
      }
    })
    return companies
  })
  const companyInActiveWatchlists = (companyId: string) => {
    return companiesInActiveWatchlists.value.has(companyId)
  }
  return {
    watchlists,
    activeWatchlists,
    loadWatchlists,
    addWatchlistModalStep,
    newWatchlistName,
    watchlistToDelete,
    watchlistMessage,
    dismissWatchlistModalGuard,
    createNewWatchlist,
    setAddWatchlistModalStep,
    renameWatchlist,
    addCompaniesToWatchlist,
    deleteWatchlist,
    resetMessage,
    removeCompanyFromWatchlist,
    resetWatchlistModalState,
    getFilteredCompanies,
    activateWatchlist,
    deactivateWatchlist,
    disableWatchlists,
    anyWatchlistActive,
    companyInActiveWatchlists,
  }
})
